import { MenuItem, Select, SelectChangeEvent, styled } from "@mui/material";
import { Assessment, Indicator, VerificationIndicator } from "../../types/LandToken";
import { useEffect, useState } from "react";
import { useAxios } from "../../services/http.service";
import ApiRoutes from "../../config/api.routes";
import { Section } from "../../pages/Scan";
import AssessmentPopup from "./AssessmentPopup";

// const tagColorMap = {
//   soil: "#f3ff59",
//   water: "#3873ff",
//   air: "#30af1a",
//   biodiversity: "#ff3737",
//   renewables: "#b30082",
// };

type Advance = {
  name: string;
  indicators: Indicator[];
  items: Assessment[];
};

type AssessmentsProps = {
  refreshData?(): void;
  tokenId: any;
  items: Assessment[];
  indicators: VerificationIndicator[];
  editable: boolean;
};

export const Assessments = ({ refreshData, items, indicators, tokenId, editable }: AssessmentsProps) => {
  const axios = useAxios();
  const [advances, setAdvance] = useState<Advance[]>([]);
  const [allIndicators, setIndicators] = useState<Indicator[]>([]);

  const calculateScore = () => {
    if (allIndicators.length) {
      const obj = {
        count: 0,
        sum: 0,
      };

      allIndicators.forEach((item) => {
        if (item?.value) {
          obj.sum += Number(item.value);
        }
        obj.count++;
      });
    }
  };

  useEffect(() => {
    calculateScore();
  }, [allIndicators]);

  useEffect(() => {
    const allItems: any = [];
    const res: any = [];
    const advances = {};
    for (const indicator of indicators) {
      if (!advances[indicator.advance]) {
        advances[indicator.advance] = {
          name: indicator.advance,
          indicators: [],
          items: [],
        };
      }

      const item = items.find((a) => a.indicatorId === indicator.id);
      advances[indicator.advance].items.push(item);
      allItems.push(item);
      advances[indicator.advance].indicators.push(indicator);
    }
    Object.keys(advances).forEach((key) => res.push(advances[key]));
    setAdvance(res);
    setIndicators(allItems);
  }, [indicators]);

  const submitAssessment = (tokenId: any) => async (indicatorId: any, value: number, reason: string) => {
    try {
      await axios
        .post(ApiRoutes.trust.assessments.create, {
          tokenId,
          indicatorId,
          value,
          reason,
        })
        .then((res) => res.data);
      if (refreshData) {
        refreshData();
      }
      return true;
    } catch (e: any) {
      console.debug({ message: "Failed to submit assessment", error: e });
      return false;
    }
  };

  const getAssessments = () => {
    if (advances.length) {
      const result: any = [];
      advances.forEach((advance, _index) => {
        if (advance.indicators.length) {
          advance.indicators.forEach((indicator, index) => {
            result.push(
              <AssessmentItem
                editable={editable}
                key={`${index}${_index}item`}
                item={advance.items[index]}
                indicator={indicator as any}
                submit={submitAssessment(tokenId)}
              />
            );
          });
          result.push(
            <tr key={_index + "rnd"}>
              <td style={{ borderBottom: "1px solid #ccc" }} />
              <td style={{ borderBottom: "1px solid #ccc" }} />
              <td style={{ borderBottom: "1px solid #ccc" }} />
            </tr>
          );
        }
      });

      return result;
    } else {
      return null;
    }
  };

  return (
    <Section title="Score the Eco KPI indicators">
      <AssessmentTable>
        <tbody>{getAssessments()}</tbody>
      </AssessmentTable>
    </Section>
  );
};

type AssessmentProps = {
  editable: boolean;
  item: Assessment;
  indicator: VerificationIndicator;
  submit(indicatorId: any, value: number, reason: string): Promise<boolean>;
};

const AssessmentItem = ({ item, indicator, submit, editable }: AssessmentProps) => {
  const [initialValue, setInitialValue] = useState(item?.value || 0);

  const [value, setValue] = useState<any>(initialValue);

  const [submitMessage, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isNaN(value) && value !== initialValue) {
      handleSubmit();
    }
  }, [value]);

  const handleValueChange = (e: SelectChangeEvent) => {
    setValue(e.target.value as string);
  };

  const handleSubmit = async () => {
    try {
      if (submitMessage === "Failed") {
        return;
      }
      setSubmitting(true);
      setMessage("Loading..");
      const result = await submit(indicator.id, value, "ok");
      if (result) {
        setInitialValue(value);
        setMessage("Success");
      } else {
        setMessage("Failed");
      }
    } catch (e: any) {
      console.debug({ message: "Failed to handle submit assessment", error: e });
      setTimeout(() => {
        setMessage("Failed");
      }, 1000);
    } finally {
      setTimeout(() => {
        setSubmitting(false);
        setMessage("");
      }, 2000);
    }
  };

  return (
    <tr>
      <td>
        <AssessmentPopup indicator={indicator} />
      </td>
      <InputCell>
        {submitMessage}
        <Select
          disabled={!editable || submitting}
          sx={{ width: "100px", borderRadius: "16px" }}
          labelId="simple-select-label"
          id="simple-select"
          value={value as string}
          onChange={handleValueChange}
        >
          <MenuItem value={0}>0</MenuItem>
          <MenuItem value={1}>1</MenuItem>
          <MenuItem value={2}>2</MenuItem>
          <MenuItem value={3}>3</MenuItem>
          <MenuItem value={4}>4</MenuItem>
          <MenuItem value={5}>5</MenuItem>
        </Select>
      </InputCell>
    </tr>
  );
};
const AssessmentTable = styled("table")`
  tbody tr:not(:nth-of-type(1)) > td:nth-of-type(3) {
    font-size: 20px;
  }
`;

const InputCell = styled("td")`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: end;
`;
